import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

// Value of the reopen choice in the rating radio group. Rating option ids are
// slugs of their labels, which never start with "__".
const REOPEN = "__reopen__";

// The satisfaction survey on /pqrsd/estado, shown once a FINAL answer is out.
// The options come from the admin config (served by handlePqrsdPublic as
// `pqrsd.survey`). A rating option closes the PQRSD; the reopen option needs a
// reason and a written detail, and reopens it with the full legal term.
//
// `initialChoice` = "reopen" preselects the reopen option (old "Necesito más
// información" email links).
export default function CitizenSurveyPanel({
  survey,
  canReopen,
  maxReopens,
  busy,
  initialChoice,
  onRate,
  onReopen,
}) {
  const [choice, setChoice] = useState(initialChoice === "reopen" && canReopen ? REOPEN : "");
  const [reasonId, setReasonId] = useState("");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  // Arriving from the email: bring the survey into view.
  useEffect(() => {
    if (initialChoice && rootRef.current) {
      rootRef.current.scrollIntoView({ block: "center" });
    }
  }, [initialChoice]);

  if (!survey) return null;
  const { question, options = [], reopen = {} } = survey;
  const minDetail = reopen.minDetail || 10;
  const maxDetail = reopen.maxDetail || 2000;
  const reopening = choice === REOPEN;
  const detailLength = detail.trim().length;

  const submit = async () => {
    setError(null);
    if (!choice) {
      setError("Elija una opción.");
      return;
    }
    try {
      if (!reopening) {
        await onRate(choice);
        return;
      }
      if (!reasonId) {
        setError("Elija el motivo por el que reabre la solicitud.");
        return;
      }
      if (detailLength < minDetail) {
        setError(`Explique con detalle qué necesita (mínimo ${minDetail} caracteres).`);
        return;
      }
      await onReopen({ reasonId, detail: detail.trim() });
    } catch (e) {
      setError(e?.message || "No fue posible enviar su respuesta. Intente de nuevo.");
    }
  };

  return (
    <Box
      ref={rootRef}
      sx={{ mt: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "action.hover" }}
    >
      <FormControl component="fieldset" fullWidth disabled={busy}>
        <FormLabel component="legend" sx={{ color: "text.primary", typography: "subtitle1", mb: 1 }}>
          {question}
        </FormLabel>
        <RadioGroup
          value={choice}
          onChange={(e) => { setChoice(e.target.value); setError(null); }}
        >
          {options.map((o) => (
            <FormControlLabel key={o.id} value={o.id} control={<Radio />} label={o.label} />
          ))}
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            value={REOPEN}
            disabled={!canReopen || busy}
            control={<Radio color="warning" />}
            label={<Typography sx={{ fontWeight: 600, color: canReopen ? "warning.dark" : "text.disabled" }}>{reopen.label}</Typography>}
          />
        </RadioGroup>
      </FormControl>

      {!canReopen && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Ya reabrió esta solicitud el máximo de {maxReopens || 5} veces. Para nuevas aclaraciones
          contáctenos por nuestros canales habituales.
        </Typography>
      )}

      {reopening && (
        <Stack spacing={2} sx={{ mt: 2, pl: { xs: 0, sm: 4 } }}>
          <FormControl component="fieldset" disabled={busy}>
            <FormLabel component="legend" sx={{ mb: 0.5 }}>¿Por qué reabre la solicitud?</FormLabel>
            <RadioGroup value={reasonId} onChange={(e) => { setReasonId(e.target.value); setError(null); }}>
              {(reopen.reasons || []).map((r) => (
                <FormControlLabel key={r.id} value={r.id} control={<Radio size="small" />} label={r.label} />
              ))}
            </RadioGroup>
          </FormControl>
          <TextField
            multiline
            minRows={4}
            fullWidth
            disabled={busy}
            label="Explique con detalle qué necesita"
            placeholder="Por ejemplo: qué parte de su pregunta quedó sin responder, qué documento esperaba recibir o qué no es claro."
            value={detail}
            onChange={(e) => { setDetail(e.target.value.slice(0, maxDetail)); setError(null); }}
            helperText={
              detailLength < minDetail
                ? `${detailLength} / mínimo ${minDetail} caracteres`
                : `${detail.length} / ${maxDetail} caracteres`
            }
          />
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color={reopening ? "warning" : "success"}
          onClick={submit}
          disabled={busy || !choice}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {reopening ? (reopen.buttonLabel || "Reabrir la solicitud") : "Enviar calificación"}
        </Button>
        {choice && (
          <Typography variant="caption" color="text.secondary">
            {reopening
              ? "La solicitud vuelve a trámite con un nuevo plazo de respuesta."
              : "Al enviar su calificación la solicitud quedará cerrada."}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
